'use client';

import { useRouter } from 'next/navigation';
import React, { useState, useEffect } from 'react';
import { encodePassphrase, generateRoomId, randomString } from '@/lib/client-utils';
import styles from '../styles/Home.module.css';

function PinGate({ onSuccess }: { onSuccess: () => void }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const rejectPin = () => {
    setError(true);
    setShake(true);
    setTimeout(() => setShake(false), 500);
    setPin('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch('/api/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin }),
      });
      if (res.ok) {
        // UX flag only — real gate is the HttpOnly session cookie set by the server.
        localStorage.setItem('flowup_meet_auth', 'true');
        onSuccess();
      } else {
        rejectPin();
      }
    } catch {
      rejectPin();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.main} data-lk-theme="default">
      <div className={styles.heroGlow} />
      <form className={styles.pinForm} onSubmit={handleSubmit}>
        <h1 className={styles.pinTitle}>
          FLOW<span className={styles.titleAccent}>UP</span>
        </h1>
        <p className={styles.pinSubtitle}>MEET</p>
        <p className={styles.pinDesc}>Введите код доступа</p>
        <div className={`${styles.pinInputWrap} ${shake ? styles.shake : ''}`}>
          <input
            type="password"
            value={pin}
            onChange={(e) => { setPin(e.target.value); setError(false); }}
            placeholder="••••"
            className={`${styles.pinInput} ${error ? styles.pinError : ''}`}
            autoFocus
            maxLength={20}
          />
        </div>
        {error && <p className={styles.errorText}>Неверный код</p>}
        <button type="submit" className={styles.ctaButton}>
          Войти
        </button>
      </form>
    </div>
  );
}

export default function Page() {
  const router = useRouter();
  const [authorized, setAuthorized] = useState(false);
  const [checking, setChecking] = useState(true);
  const [e2ee, setE2ee] = useState(false);
  const [sharedPassphrase, setSharedPassphrase] = useState(randomString(64));

  useEffect(() => {
    const auth = localStorage.getItem('flowup_meet_auth');
    setAuthorized(auth === 'true');
    setChecking(false);
  }, []);

  const startMeeting = () => {
    if (e2ee) {
      router.push(`/rooms/${generateRoomId()}#${encodePassphrase(sharedPassphrase)}`);
    } else {
      router.push(`/rooms/${generateRoomId()}`);
    }
  };

  if (checking) return null;

  if (!authorized) {
    return <PinGate onSuccess={() => setAuthorized(true)} />;
  }

  return (
    <>
      <main className={styles.main} data-lk-theme="default">
        <div className={styles.heroGlow} />
        <div className={styles.content}>
          <h1 className={styles.title}>
            FLOW<span className={styles.titleAccent}>UP</span>
          </h1>
          <p className={styles.subtitle}>MEET</p>
          <p className={styles.description}>
            Видеоконференции для обучения и бизнеса
          </p>

          <button className={styles.ctaButton} onClick={startMeeting}>
            Начать встречу
          </button>

          <div className={styles.optionsRow}>
            <label className={styles.checkLabel}>
              <input
                type="checkbox"
                checked={e2ee}
                onChange={(ev) => setE2ee(ev.target.checked)}
                className={styles.checkbox}
              />
              <span>Сквозное шифрование</span>
            </label>
            <button
              className={styles.logoutBtn}
              onClick={async () => {
                try { await fetch('/api/auth', { method: 'DELETE' }); } catch {}
                localStorage.removeItem('flowup_meet_auth');
                setAuthorized(false);
              }}
            >
              Выйти
            </button>
          </div>

          {e2ee && (
            <div className={styles.passphraseRow}>
              <input
                type="password"
                value={sharedPassphrase}
                onChange={(ev) => setSharedPassphrase(ev.target.value)}
                placeholder="Кодовая фраза"
                className={styles.passphraseInput}
              />
            </div>
          )}
        </div>
      </main>
      <footer data-lk-theme="default" className={styles.footer}>
        <a href="https://flowup.by" rel="noopener">FLOWUP</a>
        <span className={styles.footerDot}></span>
        <span>Платформа для онлайн-обучения</span>
      </footer>
    </>
  );
}
